import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Select, SelectItem } from "@carbon/react";
import { Add, DocumentImport } from "@carbon/icons-react";
import DataTable, {
  PAGE_SIZES,
} from "../../components/carbon/DataTable/DataTable";
import PageHeader from "../../components/carbon/PageHeader/PageHeader";
import EmptyState from "../../components/carbon/EmptyState/EmptyState";
import { NetworkError } from "../../components";
import { useUser } from "../../context";
import { ABILITIES, isPurchaseRequestImportEnabled } from "../../utils";
import { useLanguage } from "../../hooks";
import { useSites } from "../../hooks/useSites";
import { PurchaseRequestItemV2 } from "../../types";
import { usePurchaseRequestListData } from "./usePurchaseRequestListData";
import PurchaseRequestListFilters from "./PurchaseRequestListFilters";
import RequesterAttentionCards from "./RequesterAttentionCards";
import PurchaseRequestItemInfoPanel from "./PurchaseRequestItemInfoPanel";
import {
  itemHeaders,
  mapItemToRow,
  mapRequestToRow,
  requestHeaders,
} from "./purchaseRequestRows";
import { isItemUnselectable } from "./purchaseRequestListHelpers";
import {
  BuyerViewMode,
  buildItemStateFilters,
  buildRequestStateFilters,
  isBuyerOperatorView,
  visibleStateFilters,
} from "./purchaseRequestListTypes";
import "./purchaseRequestsV2.scss";

/**
 * Rebuilt on the Carbon primitives layer (Task 4) on `PageHeader`/`DataTable`/`EmptyState`,
 * following `containers/Materials/Materials.tsx`/`containers/Suppliers/Suppliers.tsx` as the
 * reviewed list-screen shape (see the primitives gap check, task-3-report.md). Replaces the
 * pre-Carbon 1480-line Tailwind+heroicons screen; split so every owned file stays under the
 * brief's ~400-line budget:
 * - `usePurchaseRequestListData.ts` — the data-fetching/source-priority "brain"
 *   (requests-vs-items view, tag/category filter priority, company/buyer filters).
 * - `purchaseRequestListHelpers.ts` — pure status-rollup/formatting helpers.
 * - `purchaseRequestListTypes.ts` — `BuyerViewMode`, the state-filter builders.
 * - `purchaseRequestRows.tsx` — DataTable row/header mappers for both table modes.
 * - `PurchaseRequestListFilters.tsx` — the Buyer/Manager/Analyst filter panel.
 * - `PurchaseRequestItemInfoPanel.tsx` — the items-view material-info slide-over.
 *
 * Behaviour preserved exactly: server-side pagination and every filter (status/tag/category/
 * buyer/company/site/document-class), the requests-vs-items view toggle, the status label
 * mapping (e.g. `in_approval` -> "Created" via the same `purchaseRequests.status.*` i18n keys),
 * the requester column, the items-view bulk-select -> "Launch" (`/orders/from-request`), and every
 * row navigating to the request's own detail screen (`/purchase-requests/:id` since Task 7's route
 * rename; the old `/purchase-requests-v2/:id` now permanently redirects there). The pre-Carbon
 * screen had no debounced free-text search — the
 * document-class filter was, and stays, a plain `TextInput` that updates on every keystroke.
 *
 * Two disclosed, behaviour-neutral simplifications from moving onto the shared `DataTable`:
 * the items-view table's own hand-rolled "select all" checkbox+label row is dropped in favour of
 * `DataTable`'s built-in header checkbox (same `order.selectAll` accessible name, no functional
 * loss — only the "Launch" button keeps its own small toolbar above the table); and the
 * per-row-disabled tooltip text explaining WHY a QUOTING/AWARDED/PENDING_DELIVERY/DELIVERED item
 * can't be selected is dropped (the disable itself, the actual behaviour, is preserved via
 * `DataTable`'s new `isRowDisabled`).
 */
const PurchaseRequestsV2 = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    isRequester,
    isAdmin,
    isBuyer,
    isManager,
    isAnalyst,
    user,
    companyList,
    can,
  } = useUser();
  const langEs = useLanguage();
  const { data: companySites } = useSites(user?.company?.id || "");

  // See isBuyerOperatorView: Admin/Buyer/Manager/Analyst get the tabbed view (incl. the
  // assigned-to-me "My Requests" tab); a Requester keeps the created-by-me view.
  const isBuyerView = isBuyerOperatorView({
    isAdmin,
    isBuyer,
    isManager,
    isAnalyst,
  });

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [statusFilter, setStatusFilter] = useState<string>("ALL");
  const [buyerViewMode, setBuyerViewMode] = useState<BuyerViewMode>(
    BuyerViewMode.REQUESTS
  );
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [selectedBuyerId, setSelectedBuyerId] = useState<string>("");
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<string[]>([]);
  const [requestingSiteIdFilter, setRequestingSiteIdFilter] =
    useState<string>("");
  const [infoItem, setInfoItem] = useState<PurchaseRequestItemV2 | null>(null);
  const [infoSlideOpen, setInfoSlideOpen] = useState(false);

  useEffect(() => {
    if (!infoSlideOpen) setInfoItem(null);
  }, [infoSlideOpen]);

  useEffect(() => {
    setSelectedItemIds([]);
  }, [buyerViewMode]);

  useEffect(() => {
    setStatusFilter("ALL");
  }, [buyerViewMode]);

  // Every filter change restarts at the first page — same inline `setPage(0)` the pre-Carbon
  // screen paired with each of its own `<select>`/checkbox `onChange` handlers.
  const withPageReset =
    <Args extends unknown[]>(setter: (...args: Args) => void) =>
    (...args: Args) => {
      setter(...args);
      setPage(0);
    };
  const handleBuyerViewModeChange = withPageReset(setBuyerViewMode);
  const handleBuyerIdChange = withPageReset(setSelectedBuyerId);
  const handleCompanyIdsChange = withPageReset(setSelectedCompanyIds);
  const handleStatusFilterChange = withPageReset(setStatusFilter);
  const handleTagIdsChange = withPageReset(setSelectedTagIds);
  const handleCategoryIdsChange = withPageReset(setSelectedCategoryIds);
  const handleSiteFilterChange = withPageReset(setRequestingSiteIdFilter);

  const {
    currentRequests,
    filteredContent,
    currentItems,
    filteredItems,
    isLoading,
    isError,
    buyerUsers,
  } = usePurchaseRequestListData({
    page,
    size: pageSize,
    statusFilter,
    buyerViewMode,
    isRequester,
    isBuyerView,
    selectedBuyerId,
    selectedTagIds,
    selectedCategoryIds,
    selectedCompanyIds,
    requestingSiteIdFilter,
  });

  const openInfoSlide = (item: PurchaseRequestItemV2) => {
    setInfoItem(item);
    setInfoSlideOpen(true);
  };

  const handleLaunchOrderFromItems = () => {
    if (!selectedItemIds.length) return;
    const params = new URLSearchParams();
    params.set("isV2", "true");
    params.set("itemIds", selectedItemIds.join(","));
    navigate(`/orders/from-request?${params.toString()}`);
  };

  const stateFilters = visibleStateFilters(
    buyerViewMode === BuyerViewMode.ITEMS
      ? buildItemStateFilters(t)
      : buildRequestStateFilters(t),
    isBuyerView
  );

  const requestRows = useMemo(
    () => filteredContent.map((request) => mapRequestToRow(request, t)),
    [filteredContent, t]
  );

  const itemRows = useMemo(
    () => filteredItems.map((item) => mapItemToRow(item, t, openInfoSlide)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filteredItems, t]
  );

  // Entry to the spreadsheet-import flow (the separate /purchase-requests/import screen). Kept from
  // the pre-Carbon list when it merged with the Carbon rewrite: importing is for whoever manages
  // this list (a manager handed a supplier's spreadsheet), not only requesters — hence the broader
  // gate, behind the same feature flag that guards the route.
  const importAction =
    isPurchaseRequestImportEnabled() &&
    can(ABILITIES.CREATE_PURCHASE_REQUESTS) ? (
      <Button
        as={Link}
        to="/purchase-requests/import"
        kind="tertiary"
        renderIcon={DocumentImport}
      >
        {t("purchaseRequests.import.fromSpreadsheet")}
      </Button>
    ) : null;

  const requesterAddAction = isRequester ? (
    <Button as={Link} to="/purchase-requests/new" renderIcon={Add}>
      {t("purchaseRequests.add")}
    </Button>
  ) : null;

  const addAction =
    importAction || requesterAddAction ? (
      <>
        {importAction}
        {requesterAddAction}
      </>
    ) : null;

  const isItemsView = buyerViewMode === BuyerViewMode.ITEMS;

  return (
    <div>
      <PageHeader title={t("purchaseRequests.title")} action={addAction} />

      {isRequester && !isBuyerView ? <RequesterAttentionCards /> : null}

      {isBuyerView ? (
        <PurchaseRequestListFilters
          buyerViewMode={buyerViewMode}
          onBuyerViewModeChange={handleBuyerViewModeChange}
          selectedBuyerId={selectedBuyerId}
          onBuyerIdChange={handleBuyerIdChange}
          buyerUsers={buyerUsers}
          companyList={companyList}
          selectedCompanyIds={selectedCompanyIds}
          onCompanyIdsChange={handleCompanyIdsChange}
          statusFilter={statusFilter}
          onStatusFilterChange={handleStatusFilterChange}
          stateFilters={stateFilters}
          selectedTagIds={selectedTagIds}
          onTagIdsChange={handleTagIdsChange}
          selectedCategoryIds={selectedCategoryIds}
          onCategoryIdsChange={handleCategoryIdsChange}
          companySites={companySites}
          requestingSiteIdFilter={requestingSiteIdFilter}
          onSiteFilterChange={handleSiteFilterChange}
          langEs={langEs}
        />
      ) : (
        isRequester && (
          <>
            <div className="purchase-requests__requester-filter">
              <Select
                id="pr-requester-status-filter"
                labelText={t("purchaseRequests.filters.state")}
                value={statusFilter}
                onChange={(event) =>
                  handleStatusFilterChange(event.target.value)
                }
              >
                {stateFilters.map((filter) => (
                  <SelectItem
                    key={filter.key}
                    value={filter.key}
                    text={filter.label}
                  />
                ))}
              </Select>
            </div>
          </>
        )
      )}

      {isError ? (
        <NetworkError message={t("purchaseRequests.error")} />
      ) : isItemsView ? (
        <>
          <div className="purchase-requests__items-toolbar">
            <span className="purchase-requests__items-toolbar-label">
              {t("order.selectAll")}
            </span>
            <Button
              disabled={!selectedItemIds.length}
              onClick={handleLaunchOrderFromItems}
            >
              {t("cta.launch")}
            </Button>
          </div>
          <DataTable
            headers={itemHeaders(t)}
            rows={itemRows}
            isLoading={isLoading}
            selection={{
              selectedIds: selectedItemIds,
              onChange: (ids) => setSelectedItemIds(ids as string[]),
              getRowLabel: (row) => {
                const original = filteredItems.find(
                  (item) => item.id === row.id
                );
                const label = original?.material?.globalMaterialName || row.id;
                return `${t("purchaseRequests.columns.name")}: ${label}`;
              },
              selectAllLabel: t("order.selectAll"),
              isRowDisabled: (row) => {
                const original = filteredItems.find(
                  (item) => item.id === row.id
                );
                return original ? isItemUnselectable(original) : false;
              },
            }}
            pagination={{
              page: page + 1,
              pageSize,
              pageSizes: PAGE_SIZES,
              totalItems: currentItems?.totalElements ?? 0,
              onPageChange: (newPage, newPageSize) => {
                setPage(newPage - 1);
                setPageSize(newPageSize);
              },
            }}
            empty={
              <EmptyState title={t("purchaseRequests.items.emptyQueue")} />
            }
          />
        </>
      ) : (
        <DataTable
          headers={requestHeaders(t)}
          rows={requestRows}
          isLoading={isLoading}
          onRowClick={(row) => navigate(`/purchase-requests/${row.id}`)}
          pagination={{
            page: page + 1,
            pageSize,
            pageSizes: PAGE_SIZES,
            totalItems: currentRequests?.totalElements ?? 0,
            onPageChange: (newPage, newPageSize) => {
              setPage(newPage - 1);
              setPageSize(newPageSize);
            },
          }}
          empty={
            <EmptyState title={t("purchaseRequests.noPurchaseRequests")} />
          }
        />
      )}

      <PurchaseRequestItemInfoPanel
        item={infoItem}
        open={infoSlideOpen}
        setOpen={setInfoSlideOpen}
        langEs={langEs}
      />
    </div>
  );
};

export default PurchaseRequestsV2;
